import { fetchWithAuth } from './adminApi'

export interface Membership {
  id: string
  shop_id: string
  buyer_user_id: string
  status: string
  amount_cents: number
  currency: string
  suspended: boolean
  cancel_at_period_end: boolean
  paid_until: string | null
  grace_until: string | null
}
export interface Invoice {
  id: string
  currency: string
  status: string
  amount_paid_cents: number
  refunded_cents: number
  refundable_cents: number
  dispute_status: string | null
  hosted_invoice_url: string | null
  created_at: string
  pending_refund: { request_id: string } | null
}
export interface RefundRequest {
  request_id: string
  shop_id: string
  invoice_id: string
  amount_cents: number
  reason: string
}
export interface RefundAttempt {
  request_id: string
  invoice_id: string
  actor_user_id: string
  actor_role: string
  amount_cents: number
  reason: string
  status: string
  refund_id: string | null
  created_at: string
}
export interface MembershipDetail {
  membership: Membership
  invoices: Invoice[]
  refund_attempts: RefundAttempt[]
}
export type SupportAction = 'reconcile' | 'suspend' | 'restore'
export class MembershipRequestError extends Error {
  status: number
  constructor(message: string, status: number) {
    super(message)
    this.status = status
  }
  // 4xx is a known rejection; 5xx and network failures may still have applied.
  get definitive() {
    return this.status >= 400 && this.status < 500
  }
}
async function request<T>(path: string, body?: unknown): Promise<T> {
  const result = await fetchWithAuth<T>(
    `/admin/backstage/memberships${path}`,
    body === undefined
      ? {}
      : {
          method: 'POST',
          body: JSON.stringify(body),
        }
  )
  if (!result.ok || result.data === undefined)
    throw new MembershipRequestError(
      result.error?.message || 'Membership request failed',
      Number(result.error?.code) || 0
    )
  return result.data
}
export const memberships = {
  timeline: (id: string, offset = 0) =>
    request<
      {
        id: string
        source: string
        kind: string
        created_at: string
        details: Record<string, unknown>
      }[]
    >(`/${encodeURIComponent(id)}/timeline?offset=${offset}`),
  operations: async () => {
    const result = await fetchWithAuth<Record<string, number>>(
      '/admin/backstage/membership-operations',
      {}
    )
    if (!result.ok || !result.data)
      throw new Error(result.error?.message || 'Operations unavailable')
    return result.data
  },
  search: (scope: 'shop_id' | 'buyer_user_id', id: string, offset = 0) =>
    request<Membership[]>(`?${scope}=${encodeURIComponent(id)}&offset=${offset}`),
  detail: (id: string, offset = 0) =>
    request<MembershipDetail>(`/${encodeURIComponent(id)}/invoices?offset=${offset}`),
  action: (id: string, action: SupportAction, reason: string) =>
    request<Membership>(`/${encodeURIComponent(id)}/actions`, { action, reason }),
  refund: (id: string, body: RefundRequest) =>
    request<{ status: string; refund_id: string }>(`/${encodeURIComponent(id)}/refunds`, body),
}
export const money = (cents: number, currency: string) =>
  new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100)
